import {useCallback, useMemo, useState} from 'react';
import {activeFilterChips, applyFilters, isFilterable, nextSort, optionsFor, rangeFor, sortRows} from './tableControls';

/**
 * Sorting + per-column filtering for a page's existing table.
 *
 *   const t = useTableControls(rows, COLUMNS);
 *   <SortFilterHeaderCell {...t.headerProps('school')} sx={headCell}/>
 *   {t.rows.map((row) => …)}            // the same row objects, re-ordered and filtered
 *
 * Options and ranges come from the unfiltered rows, so a ticked value never
 * disappears from its own list. Call it below the page's loading/no-data
 * branches (inside a child component) — hooks cannot sit after an early return.
 */
export const useTableControls = (rows, columns, {minRowsForFilter = 5, initialSort = null} = {}) => {
    const [sort, setSort] = useState(initialSort);
    const [filters, setFilters] = useState({});
    const safeRows = rows || [];

    const toggleSort = useCallback((key) => setSort((s) => nextSort(s, key)), []);
    const setFilter = useCallback((key, value) => setFilters((f) => {
        const next = {...f};
        if (!value) delete next[key]; else next[key] = value;
        return next;
    }), []);
    const clearAll = useCallback(() => setFilters({}), []);

    const shown = useMemo(() => sortRows(applyFilters(safeRows, filters, columns), sort, columns), [safeRows, filters, sort, columns]);
    const chips = useMemo(() => activeFilterChips(filters, columns), [filters, columns]);

    const headerProps = (key) => {
        const col = (columns || []).find((c) => c.key === key) || {key, label: key, type: 'text'};
        return {
            col, sort, onSort: toggleSort,
            filter: filters[key] || null,
            onFilter: (v) => setFilter(key, v),
            options: optionsFor(safeRows, col),
            range: rangeFor(safeRows, col),
            filterable: isFilterable(col, safeRows, minRowsForFilter),
        };
    };

    return {
        rows: shown, total: safeRows.length, sort, setSort, toggleSort,
        filters, setFilter, clearAll, activeCount: chips.length, chips, headerProps,
    };
};

export default useTableControls;
